import React, { useEffect, useState } from "react";
import BillingHeader from "../Billing/BillingHeader";
import BillingFilter from "../Billing/BillingFilter";
import Typography from "../../Utility/Typography";
import Button from "../../Utility/Button/Button";
import { RiPencilLine, RiDeleteBinFill } from "react-icons/ri";
import { useSorting, SortableHeader } from "../../Utility/Sorting";
import Pagination from "../../Utility/Pagination/Pagination";
import UserRateMappingAddModel from "./AddMappingModels/UserRateMappingModel";
import { useDispatch, useSelector } from "react-redux";
import {
  addUserRateMapping,
  deleteUserRateMapping,
  selectUserRateMappingData,
  selectUserRateMappingError,
  selectUserRateMappingLoading,
} from "../../Redux/Slices/userRateMappingSlice";
import { fetchUserRateMappings } from "../../Redux/Thunks/userRateMappingThunk";

const UserRateMapping = () => {
  /* ===================== FILTER STATE ===================== */
  const [filters, setFilters] = useState({
    user: "",
  });

  /* ===================== TABLE STATE ===================== */
  const dispatch = useDispatch();
  const userRateData = useSelector(selectUserRateMappingData);
  const loading = useSelector(selectUserRateMappingLoading);
  const error = useSelector(selectUserRateMappingError);

  const rates = userRateData?.list || [];

  /* ===================== FETCH DATA ON MOUNT ===================== */
  useEffect(() => {
    if (!userRateData && !loading) {
      dispatch(fetchUserRateMappings());
    }
  }, [dispatch, userRateData, loading]);

  /* ===================== MODAL STATE ===================== */
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);

  const handleAddMapping = () => {
    setIsAddModalOpen(true);
  };

  /* ===================== USER OPTIONS ===================== */
  // Build unique user list for filter dropdown
  const userOptions = ["Select User", ...Array.from(new Set(rates.map((row) => row.user))).sort()];

  /* ===================== ROLE OPTIONS ===================== */
  const roleOptions = ["Select Role", ...Array.from(new Set(rates.map((row) => row.role))).sort()];

  /* ===================== CLIENT OPTIONS ===================== */
  const clientOptions = [
    "Select Client",
    ...Array.from(new Set(rates.map((row) => row.client))).sort(),
  ];

  /* ===================== FILTER HANDLER ===================== */
  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters((prev) => ({ ...prev, [name]: value }));
    // Reset to first page when filters change
    setCurrentPage(1);
  };

  /* ===================== FILTERED DATA ===================== */
  const filteredRates = rates.filter((row) => {
    // Filter by selected user
    if (filters.user && filters.user !== "Select User") {
      if (row.user !== filters.user) return false;
    }
    return true;
  });

  /* ===================== SORTING ===================== */
  const { sortedData, sortKey, sortDirection, handleSort } = useSorting(filteredRates, {
    defaultSortKey: "user",
    defaultSortDirection: "asc",
  });

  /* ===================== PAGINATION ===================== */
  const [currentPage, setCurrentPage] = useState(1);
  const pageSize = 5;

  const totalPages = Math.max(1, Math.ceil(sortedData.length / pageSize));
  const startIndex = (currentPage - 1) * pageSize;
  const endIndex = startIndex + pageSize;

  const paginatedRates = sortedData.slice(startIndex, endIndex);

  const handlePageChange = (page) => {
    if (page < 1 || page > totalPages) return;
    setCurrentPage(page);
  };

  /* ===================== ACTION HANDLERS ===================== */
  const handleEdit = (row) => {
    console.log("Edit rate:", row);
  };

  const handleDelete = (id) => {
    dispatch(deleteUserRateMapping(id));
  };

  const handleSaveNewMapping = ({ user, role, client, rate }) => {
    const newItem = {
      id: Date.now(),
      user,
      role,
      client,
      rate: Number(rate),
    };

    dispatch(addUserRateMapping(newItem));

    // Move to last page where the new item will appear (based on current length)
    const newTotalPages = Math.max(1, Math.ceil((rates.length + 1) / pageSize));
    setCurrentPage(newTotalPages);
  };

  return (
    <>
      {/* ===================== HEADER ===================== */}
      <BillingHeader
        title="User Rate Mapping"
        subtitle="Set billing rates for users or roles. These rates will be used when generating exports."
      />

      {/* ===================== FILTER ===================== */}
      <BillingFilter
        onAddMapping={handleAddMapping}
        filters={filters}
        onFilterChange={handleFilterChange}
        userOptions={userOptions}
        showUser
        notShowClient={false}
        notShowQBClient={false}
        notShowTag={false}
        notShowAccount={false}
      />

      {/* ===================== TABLE ===================== */}
      <div className="w-full">
        <div className="relative flex flex-col bg-surface-primary shadow-sm">
          <div className="flex-1 p-2">
            <div className="overflow-x-auto">
              {loading && (
                <div className="py-6 text-center">
                  <Typography className="text-text-secondary">
                    Loading user rate mappings...
                  </Typography>
                </div>
              )}
              {error && !loading && (
                <div className="py-6 text-center">
                  <Typography className="text-red-500">{error}</Typography>
                </div>
              )}
              <table className="w-full mb-0 clients-table">
                {/* ===================== TABLE HEADER ===================== */}
                <thead className="bg-surface-secondary">
                  <tr>
                    <SortableHeader
                      columnKey="user"
                      label="User"
                      onSort={handleSort}
                      currentSortKey={sortKey}
                      sortDirection={sortDirection}
                    />
                    <SortableHeader
                      columnKey="role"
                      label="Role"
                      onSort={handleSort}
                      currentSortKey={sortKey}
                      sortDirection={sortDirection}
                    />
                    <SortableHeader
                      columnKey="client"
                      label="Client"
                      onSort={handleSort}
                      currentSortKey={sortKey}
                      sortDirection={sortDirection}
                    />
                    <SortableHeader
                      columnKey="rate"
                      label="Rate ($/hr)"
                      onSort={handleSort}
                      currentSortKey={sortKey}
                      sortDirection={sortDirection}
                    />
                    <SortableHeader
                      columnKey="action"
                      label="Actions"
                      sortable={false}
                      className="action-col"
                    />
                  </tr>
                </thead>

                {/* ===================== TABLE BODY ===================== */}
                <tbody>
                  {sortedData.length === 0 ? (
                    <tr>
                      <td colSpan={5} className="text-center py-6">
                        <Typography className="text-text-secondary">
                          No user rate mappings found
                        </Typography>
                      </td>
                    </tr>
                  ) : (
                    paginatedRates.map((row) => (
                      <tr key={row.id}>
                        <td>
                          <Typography className="font-medium">{row.user}</Typography>
                        </td>

                        <td>
                          <Typography>{row.role}</Typography>
                        </td>

                        <td>
                          <Typography>{row.client}</Typography>
                        </td>

                        <td>
                          <Typography className="font-medium">${row.rate}</Typography>
                        </td>

                        <td className="text-right">
                          <div className="flex items-center justify-end gap-2">
                            <Button
                              size="sm"
                              className="btn-warning-new !w-7 !h-7 !p-0 flex items-center justify-center"
                              title="Edit"
                              onClick={() => handleEdit(row)}
                            >
                              <RiPencilLine className="text-white text-[12px]" />
                            </Button>

                            <Button
                              size="sm"
                              className="btn-danger-new !w-7 !h-7 !p-0 flex items-center justify-center"
                              title="Delete"
                              onClick={() => handleDelete(row.id)}
                            >
                              <RiDeleteBinFill className="text-white text-[12px]" />
                            </Button>
                          </div>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>

          {/* ===================== PAGINATION ===================== */}
          <div className="px-4 py-3 border-t bg-surface-secondary">
            <Pagination
              currentPage={currentPage}
              totalPages={totalPages}
              onPageChange={handlePageChange}
            />
          </div>

          {/* ===================== FOOTER ===================== */}
          <div className="flex justify-end items-center gap-3 px-4 py-3 border-t bg-surface-secondary">
            <Button variant="" className="btn-reset">
              Cancel
            </Button>
            <Button variant="primary">Save Configuration</Button>
          </div>
        </div>
      </div>

      {/* ===================== ADD MAPPING MODAL ===================== */}
      {isAddModalOpen && (
        <UserRateMappingAddModel
          onClose={() => setIsAddModalOpen(false)}
          onSave={handleSaveNewMapping}
          userOptions={userOptions}
          roleOptions={roleOptions}
          clientOptions={clientOptions}
        />
      )}
    </>
  );
};

export default UserRateMapping;
